/**
 * Document folder grouping for the "Chat with your docs" picker.
 *
 * Mirrors the client's canonical grouping so the sidebar organizes documents
 * into the same category folders as the loan Documents tab. Keep the folder ids
 * and labels in sync with:
 *   client/components/people/LoanView/documentFolderTypes.ts (FOLDER_DEFINITIONS)
 *   backend/functions/config/globalVariables.js (DOCUMENT_GROUP_NAMES)
 */

/** Canonical folder ids and display labels, in default display order. */
export const FOLDER_DEFINITIONS = [
  { id: "asset", name: "Asset" },
  { id: "borrower-info", name: "Borrower Info" },
  { id: "credit", name: "Credit" },
  { id: "disclosures", name: "Disclosures" },
  { id: "income", name: "Income/Employment" },
  { id: "property", name: "Property" },
  { id: "other", name: "Other" },
  { id: "va", name: "VA Specific" },
];

const KNOWN_FOLDER_IDS = new Set(FOLDER_DEFINITIONS.map((folder) => folder.id));

function getTrimmedValue(value) {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();

  return trimmed.length > 0 ? trimmed : null;
}

function getDocumentTypeFromClassification(classification) {
  if (Array.isArray(classification)) {
    for (const item of classification) {
      const documentType = getTrimmedValue(item?.documentType);
      if (documentType) {
        return documentType;
      }
      const fallbackType = getTrimmedValue(item?.type);
      if (fallbackType) {
        return fallbackType;
      }
    }

    return null;
  }

  if (classification && typeof classification === "object") {
    return (
      getTrimmedValue(classification.documentType) ||
      getTrimmedValue(classification.type)
    );
  }

  return null;
}

function getDocumentTypeFromSplits(splits) {
  if (!Array.isArray(splits)) {
    return null;
  }
  for (const split of splits) {
    const docType = getTrimmedValue(split?.docType);
    if (docType) {
      return docType;
    }
    const fallbackType = getTrimmedValue(split?.type);
    if (fallbackType) {
      return fallbackType;
    }
  }

  return null;
}

function getDocumentTypeFromExtendAiResult(extendAISplitResult) {
  if (!extendAISplitResult || typeof extendAISplitResult !== "object") {
    return null;
  }

  return (
    getDocumentTypeFromClassification(extendAISplitResult.classification) ||
    getDocumentTypeFromClassification(extendAISplitResult.classify)
  );
}

/**
 * Resolves the folder group id for a document, preferring the explicit backend
 * group and falling back to classification data (mirrors client priority).
 *
 * @param {Object} doc
 * @param {boolean} [allowDynamicGroups=true] - When false, unknown groups collapse to "other".
 * @returns {string} A folder id (may be a dynamic/custom-org value when allowed).
 */
export function resolveDocumentGroup(doc, allowDynamicGroups = true) {
  const resolvedGroup =
    getTrimmedValue(doc?.group) ||
    getTrimmedValue(doc?.category) ||
    getDocumentTypeFromClassification(doc?.classification) ||
    getDocumentTypeFromSplits(doc?.splits) ||
    getDocumentTypeFromExtendAiResult(doc?.extendAISplitResult) ||
    "other";

  if (allowDynamicGroups || KNOWN_FOLDER_IDS.has(resolvedGroup)) {
    return resolvedGroup;
  }

  return "other";
}

/**
 * Returns the finer document type shown beneath a document name (e.g. "W-2
 * Form"). Falls back to the free-text category; returns "" when nothing is
 * resolvable so the row can omit the sub-line rather than repeat the folder.
 *
 * @param {Object} doc
 * @returns {string}
 */
export function getDocumentTypeLabel(doc) {
  const type =
    getDocumentTypeFromClassification(doc?.classification) ||
    getDocumentTypeFromSplits(doc?.splits) ||
    getDocumentTypeFromExtendAiResult(doc?.extendAISplitResult) ||
    getTrimmedValue(doc?.category);

  return type || "";
}

/**
 * Groups documents into ordered folder sections.
 *
 * Order: dynamic/custom-org folders first (alphabetical), then the canonical
 * FOLDER_DEFINITIONS order. `va` is included only for VA loans. Empty folders
 * are omitted.
 *
 * @param {Array} documents
 * @param {Object} [options]
 * @param {boolean} [options.isVaLoan=false]
 * @returns {Array<{ id: string, name: string, docs: Array }>}
 */
export function groupDocumentsByFolder(documents, { isVaLoan = false } = {}) {
  const docsList = Array.isArray(documents) ? documents : [];
  const byGroup = new Map();

  for (const doc of docsList) {
    const groupId = resolveDocumentGroup(doc);
    if (!byGroup.has(groupId)) {
      byGroup.set(groupId, []);
    }
    byGroup.get(groupId).push(doc);
  }

  const dynamicSections = Array.from(byGroup.keys())
    .filter((id) => !KNOWN_FOLDER_IDS.has(id))
    .sort()
    .map((id) => ({ id, name: id, docs: byGroup.get(id) }));

  const knownSections = FOLDER_DEFINITIONS.filter((folder) => {
    if (folder.id === "va" && !isVaLoan) {
      return false;
    }

    return byGroup.has(folder.id);
  }).map((folder) => ({
    id: folder.id,
    name: folder.name,
    docs: byGroup.get(folder.id),
  }));

  return [...dynamicSections, ...knownSections];
}
